import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface TabbedFilterOption {
  label: string;
  value: string;
  isChecked: boolean;
}

export interface TabbedFilterGroup {
  /** Tab title, e.g. "Status", "Created by". */
  label: string;
  /** URL query parameter this tab reads and writes, e.g. "status". */
  queryParam: string;
  options: TabbedFilterOption[];
  /** Show a search box above the checkboxes (useful for long people lists). */
  searchable?: boolean;
  /** At most one value: ticking an option unticks the others and the URL param is a scalar. */
  single?: boolean;
  /** data-testid for the group's checkbox list. */
  testId?: string;
}

/**
 * One "Filters (N)" button whose popup has a tab per filter group -- the same
 * shape as the Payapp tab's filter. Unlike the Payapp one, every selection is
 * written to the URL (`?status=a&status=b&created_by=x`) so the list it sits
 * above can react to route changes exactly as it does for <app-status-filter>,
 * and the filter survives a refresh.
 *
 * N counts groups with at least one selection, not individual checkboxes
 * (plus `extraActiveCount`). Content marked `tabbedFilterFooter` is projected
 * below the tabs.
 */
@Component({
  selector: 'app-tabbed-filter',
  templateUrl: './tabbed-filter.component.html',
  styleUrls: ['./tabbed-filter.component.scss'],
  standalone: false,
})
export class TabbedFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() groups: TabbedFilterGroup[] = [];
  /**
   * Opt-in: filters the host applies outside the tabs (chips, a footer checkbox)
   * that should still count toward "Filters (N)".
   */
  @Input() extraActiveCount = 0;
  /**
   * Opt-in: tighter tab labels and no Material pagination arrows, so a short tab
   * strip (3 tabs) fits the 320px popup without horizontal scrolling.
   */
  @Input() fitTabs = false;

  isOpen = false;
  buttonLabel = 'Filters (0)';
  /** One search control per group, keyed by queryParam. */
  searchForm = new FormGroup({});

  private latestQueryParams: Record<string, any> = {};
  private readonly onDestroy = new Subject<void>();

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        this.latestQueryParams = qp;
        this.syncFromUrl();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.groups) {
      this.ensureSearchControls();
      this.syncFromUrl();
    } else if (changes.extraActiveCount) {
      this.syncFromUrl();
    }
  }

  onSelectionChange(group: TabbedFilterGroup, selected: string[]) {
    let value: string | string[] | null = selected.length ? selected : null;
    if (group.single) {
      const previous = this.selectedFor(group);
      const added = selected.filter(v => !previous.includes(v));
      value = added.length ? added[added.length - 1] : (selected[0] ?? null);
    }
    this.router.navigate([], {
      queryParams: { [group.queryParam]: value },
      queryParamsHandling: 'merge',
    });
  }

  private selectedFor(group: TabbedFilterGroup): string[] {
    const raw = this.latestQueryParams[group.queryParam];
    return raw ? (Array.isArray(raw) ? raw : [raw]) : [];
  }

  private ensureSearchControls() {
    for (const g of this.groups) {
      if (!this.searchForm.get(g.queryParam)) {
        this.searchForm.addControl(g.queryParam, new FormControl(''));
      }
    }
  }

  /** Mirror the URL into each group's checkboxes and recompute the label. */
  private syncFromUrl() {
    let active = this.extraActiveCount || 0;
    for (const g of this.groups) {
      const selected = this.selectedFor(g);
      g.options.forEach(o => (o.isChecked = selected.includes(o.value)));
      if (selected.length) active++;
    }
    this.buttonLabel = `Filter${active === 1 ? '' : 's'} (${active})`;
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
